import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

type Variant = 'brand' | 'success' | 'warning' | 'danger' | 'neutral' | 'info';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: Variant;
  leftIcon?: ReactNode;
  /** Liten färgad punkt före texten, som statusmärkena i referensbilderna. */
  dot?: boolean;
}

/**
 * Pillerformat statusmärke: mjuk ton, ingen ram, versal spärrad text.
 *
 * 2.0 använde en fyrkantig etikett med hårlinje för att inte dra uppmärksamhet.
 * 3.0 vänder på det — märket ÄR informationen i en lista, och den tonade
 * pillerformen läses snabbare än en ram.
 */
const variants: Record<Variant, string> = {
  brand: 'bg-brand-50 text-brand-700',
  success: 'bg-emerald-50 text-emerald-700',
  warning: 'bg-amber-50 text-amber-700',
  danger: 'bg-red-50 text-red-700',
  neutral: 'bg-ink-100 text-ink-600',
  info: 'bg-indigo-50 text-indigo-700',
};

const dots: Record<Variant, string> = {
  brand: 'bg-brand-500',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
  neutral: 'bg-ink-500',
  info: 'bg-indigo-500',
};

export function Badge({
  variant = 'neutral',
  leftIcon,
  dot,
  className,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.07em] whitespace-nowrap',
        variants[variant],
        className,
      )}
      {...rest}
    >
      {dot && <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', dots[variant])} aria-hidden />}
      {leftIcon && (
        <span className="inline-flex shrink-0" aria-hidden>
          {leftIcon}
        </span>
      )}
      {children}
    </span>
  );
}
